import {FC, ReactNode} from 'react'
import {createPortal} from 'react-dom'
import ActionButton from 'jbc-front/components/ActionButton'
import {Download, Edit, Print} from 'jbc-front/components/icons'
import {PdfModal} from 'jbc-front/components/presenters/ui/PdfModal'
import {Pdf} from 'jbc-front/components/presenters/ui/Pdf'
import {usePdfPreview} from 'hooks/usePdfPreview'
import styles from './PreviewPdfModal.scss'

export interface PreviewPdfData {
  title: string // 帳票タイトル
  url: string // ダウンロードURL
  pdf?: string // PDFデータ (URL)
  edit?: boolean // 編集許可 (undefined = 非表示, true = 編集可, false = 編集不可)
}

interface PreviewPdfModalProps {
  data: PreviewPdfData | undefined
  extraButtons?: ReactNode
  isVisibleMyNumber?: boolean
  onClose: () => void
  onEditClick?: (data: PreviewPdfData) => void
}

export const PreviewPdfModal: FC<PreviewPdfModalProps> = ({
  data,
  extraButtons,
  isVisibleMyNumber,
  onClose,
  onEditClick,
}) => {
  if (!data) {
    return null
  }

  const handleEdit = () => {
    if (data.edit) {
      onEditClick?.(data)
    }
  }

  const handlePrint = () => {
    let url = `${data.url}&disposition=inline`
    if (isVisibleMyNumber) {
      url += '&is_visible_my_number=true'
    }

    const w = window.open(url, '', 'toolbar=0,menubar=0,location=0')
    w?.addEventListener('load', () => w.print())
  }

  const downloadUrl = isVisibleMyNumber ? `${data.url}&is_visible_my_number=true` : data.url

  return createPortal(
    <PdfModal isOpen>
      <PdfModal.Header className={styles.header}>
        <PdfModal.Title className={styles.title}>{data.title}</PdfModal.Title>
        <PdfModal.ToolContainer className={styles.tools} onClose={onClose}>
          {extraButtons}

          {data.edit !== undefined && (
            <ActionButton icon={<Edit size={15} />} onClick={handleEdit} disabled={!data.edit}>
              編集
            </ActionButton>
          )}

          <ActionButton primary icon={<Print size={15} />} onClick={handlePrint}>
            印刷
          </ActionButton>

          <ActionButton primary icon={<Download size={15} />} as="a" download href={downloadUrl}>
            ダウンロード
          </ActionButton>
        </PdfModal.ToolContainer>
      </PdfModal.Header>
      {data.pdf && <ModalBody pdf={data.pdf} />}
    </PdfModal>,
    document.body
  )
}

interface ModalBodyProps {
  pdf: string
}

const ModalBody: FC<ModalBodyProps> = ({pdf}) => {
  const {loading, canvasRef, controllerProps} = usePdfPreview(pdf)

  return (
    <Pdf.Container>
      <Pdf loading={loading} ref={canvasRef} />
      <Pdf.ControllerContainer>
        <Pdf.Controller {...controllerProps} />
      </Pdf.ControllerContainer>
    </Pdf.Container>
  )
}
